"use client";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import constants from "@/app/styles/constants";
import CreditsModal from "@/components/CreditsModal";
import ExternalLink from "@/components/ExternalLink";

import Navigation from "./Navigation";

const smallGridTemplateAreas = `
"nav"
"madeby"
"copyright"
"source"
`;
const largeGridTemplateAreas = `
"nav madeby"
"nav copyright"
"nav source"
`;

interface FooterProps {
  height?: string;
}

export const DEFAULT_HEIGHT = constants.footer.height;

const Footer: React.FC<FooterProps> = ({ height = DEFAULT_HEIGHT }) => {
  const color = "background.default";
  return (
    <Box
      data-testid="footer"
      component="footer"
      sx={{
        backgroundColor: "secondary.main",
        color,
        height,
        padding: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container
        maxWidth="sm"
        disableGutters
        sx={{
          backgroundColor: "secondary.main",
          display: "grid",
          gap: 0.5,
          textAlign: "center",
          height: "100%",
          width: "100%",
          gridTemplateAreas: {
            xs: smallGridTemplateAreas,
            sm: largeGridTemplateAreas,
          },
        }}
      >
        <Box
          sx={{
            gridArea: "nav",
            display: "flex",
            justifyContent: {
              xs: "center",
              sm: "flex-start",
            },
            alignItems: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Navigation />
        </Box>
        <Box
          sx={{
            gridArea: "madeby",
            display: "flex",
            justifyContent: {
              xs: "center",
              sm: "flex-end",
            },
            alignItems: {
              xs: "center",
              sm: "flex-end",
            },
          }}
        >
          <Typography
            variant="sacramento"
            component="p"
            sx={{
              fontSize: {
                xs: "24px",
                sm: "34px",
              },
            }}
          >
            Made with{" "}
            <Typography
              component="span"
              sx={{ color: "primary.main", display: "inline" }}
            >
              &#9829;
            </Typography>{" "}
            by Nichole Frey
          </Typography>
        </Box>
        <Box
          sx={{
            gridArea: "copyright",
            display: "flex",
            justifyContent: {
              xs: "center",
              sm: "flex-end",
            },
            alignItems: {
              xs: "center",
              sm: "flex-end",
            },
          }}
        >
          <Typography
            variant="overline"
            component="p"
            sx={{ textTransform: "none" }}
          >
            Copyright &#169; {new Date().getFullYear()} -{" "}
            <strong>All Rights Reserved</strong>
          </Typography>
        </Box>
        <Box
          sx={{
            gridArea: "source",
            display: "flex",
            justifyContent: {
              xs: "center",
              sm: "flex-end",
            },
            alignItems: {
              xs: "center",
              sm: "flex-end",
            },
          }}
        >
          <Typography variant="overline" component="p" sx={{ display: "flex" }}>
            <ExternalLink
              href="https://github.com/nicholeuf/zen-site-next"
              sx={{
                color,
              }}
              aria-label="View Source Code"
            >
              view source code
            </ExternalLink>
            &nbsp;|&nbsp;
            <CreditsModal />
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
